import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { UserPlus, Plus, Check, Trash2, X, Loader2 } from "lucide-react";

const TYPES = ["friend", "business", "customer"];
const REWARD_TYPES = ["points", "cashback", "coupon"];
const STATUS_COLOR = { pending: "amber", completed: "blue", rewarded: "green" };
const EMPTY = { referrerCustomerId: "", refereeName: "", refereeEmail: "", type: "friend", rewardType: "points", rewardValue: 500 };

export default function ReferralPage() {
  const { hasPermission } = useAuth();
  const { format } = useCurrency();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await loyaltyService.referrals()).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.referrerCustomerId.trim() || !form.refereeName.trim()) { toast.error("Referrer ID and referee name required"); return; }
    setSaving(true);
    try { await loyaltyService.createReferral({ ...form, rewardValue: Number(form.rewardValue) }); toast.success("Referral created"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const complete = async (r) => {
    try { await loyaltyService.completeReferral(r.id); toast.success("Referral completed & rewarded"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const reward = (r) => r.rewardType === "cashback" ? format(r.rewardValue) : `${r.rewardValue} ${r.rewardType}`;

  const columns = [
    { key: "code", header: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
    { key: "referrer", header: "Referrer", render: (r) => <span className="font-mono text-sm">{r.referrerCustomerId}</span> },
    { key: "referee", header: "Referee", render: (r) => <span className="text-sm">{r.refereeName}</span> },
    { key: "type", header: "Type", render: (r) => <Badge color="blue">{r.type}</Badge> },
    { key: "reward", header: "Reward", render: (r) => <span className="text-sm">{reward(r)}</span> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "act", header: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        {r.status !== "rewarded" && hasPermission("LOYALTY.UPDATE") && <button onClick={() => complete(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline" data-testid={`referral-complete-${r.id}`}><Check className="h-3.5 w-3.5" /> Complete</button>}
        {hasPermission("LOYALTY.ADMIN") && <button onClick={async () => { if (window.confirm("Delete referral?")) { await loyaltyService.deleteReferral(r.id); toast.success("Deleted"); load(); } }} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="h-4 w-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div data-testid="referral-page">
      <PageHeader title="Referrals" subtitle="Refer friend / business / customer — reward referrers automatically"
        actions={hasPermission("LOYALTY.CREATE") && (
          <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="referral-create-button"><Plus className="h-4 w-4" /> New Referral</button>
        )} />

      <DataTable testid="referrals-table" rows={rows} loading={loading} columns={columns} empty="No referrals yet." />

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referral-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border"><div className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /><h3 className="font-semibold text-foreground">New Referral</h3></div><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-muted-foreground" /></button></div>
            <div className="px-6 py-5 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Referrer Customer ID</label><input value={form.referrerCustomerId} onChange={(e) => setForm({ ...form, referrerCustomerId: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="referral-referrer-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Referee Name</label><input value={form.refereeName} onChange={(e) => setForm({ ...form, refereeName: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="referral-referee-input" /></div>
                <div><label className="block text-sm font-medium mb-1">Referee Email</label><input value={form.refereeEmail} onChange={(e) => setForm({ ...form, refereeEmail: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium mb-1">Type</label><LSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</LSelect></div>
                <div><label className="block text-sm font-medium mb-1">Reward</label><LSelect value={form.rewardType} onChange={(e) => setForm({ ...form, rewardType: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm">{REWARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</LSelect></div>
                <div><label className="block text-sm font-medium mb-1">Value</label><input type="number" value={form.rewardValue} onChange={(e) => setForm({ ...form, rewardValue: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm" data-testid="referral-value-input" /></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2"><button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted">Cancel</button><button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="referral-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Create</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
